import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import TiktokDateRangeFilter, { pickDatePart } from '@/tiktok-platform/components/TiktokDateRangeFilter';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

const STATUS_OPTIONS = [
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: 'CANCELLATION_REQUEST_PENDING', label: '待处理', labelKey: 'orderList.tabPending' },
  { value: 'CANCELLATION_REQUEST_SUCCESS', label: '已同意', labelKey: 'cancel.stAgreed' },
  { value: 'CANCELLATION_REQUEST_CANCEL', label: '已撤销', labelKey: 'cancel.stWithdrawn' },
  { value: 'CANCELLATION_REQUEST_CANCELLED', label: '已取消', labelKey: 'cancel.stCancelled' },
  { value: 'CANCELLATION_REQUEST_COMPLETE', label: '已完成', labelKey: 'orderList.tabCompleted' },
];

const TYPE_LABELS = {
  CANCEL: '卖家或系统取消',
  BUYER_CANCEL: '买家申请取消',
};

const TYPE_KEYS = {
  CANCEL: 'cancel.typeCancel',
  BUYER_CANCEL: 'cancel.typeBuyer',
};

const ROLE_LABELS = {
  BUYER: '买家',
  SELLER: '卖家',
  SYSTEM: '系统',
};

const ROLE_KEYS = {
  BUYER: 'cancel.roleBuyer',
  SELLER: 'cancel.roleSeller',
  SYSTEM: 'cancel.roleSystem',
};

function codedLabel(code, labels, keys, t) {
  if (!code) return '—';
  if (keys[code]) return t(keys[code], { defaultValue: labels[code] });
  return code;
}

function formatTime(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return raw ? String(raw) : '—';
  const ms = n < 1e12 ? n * 1000 : n;
  try {
    return new Date(ms).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return String(raw);
  }
}

function dayStartSeconds(value) {
  const day = pickDatePart(value);
  if (!day) return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  return Math.floor(date.getTime() / 1000);
}

function dayEndExclusiveSeconds(value) {
  const day = pickDatePart(value);
  if (!day) return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return Math.floor(date.getTime() / 1000);
}

function statusLabel(code, t) {
  const opt = STATUS_OPTIONS.find((item) => item.value === code);
  if (opt?.labelKey) return t(opt.labelKey, { defaultValue: opt.label });
  return code || '—';
}

function statusTag(code, t) {
  if (!code) return '—';
  const tone = code === 'CANCELLATION_REQUEST_PENDING'
    ? 'pending'
    : (code === 'CANCELLATION_REQUEST_SUCCESS' || code === 'CANCELLATION_REQUEST_COMPLETE' ? 'ok' : 'fail');
  return <span className={`tko-tag tko-tag--${tone}`}>{statusLabel(code, t)}</span>;
}

function refundText(row) {
  const amount = row.refund_amount || row.refundAmount || {};
  const total = amount.refund_total ?? amount.refundTotal;
  const currency = amount.currency || '';
  if (total == null || total === '') return '—';
  return `${total}${currency ? ` ${currency}` : ''}`;
}

function rowId(row) {
  return String(row?.cancelId || row?.cancel_id || '');
}

const EMPTY_QUERY = { orderId: '', startDate: '', endDate: '' };

export default function CancellationListPage() {
  const { t } = useTranslation('workbench');
  const { concreteScopeId, shopDimensionTabs, loading: shopLoading } = useTiktokPageShopScope();
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();
  const shopId = concreteScopeId || '';
  const [status, setStatus] = useState('');
  const [draft, setDraft] = useState(EMPTY_QUERY);
  const [applied, setApplied] = useState(EMPTY_QUERY);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastSyncedAt, setLastSyncedAt] = useState('');

  const queryParams = useCallback((nextPage, nextSize = pageSize) => ({
    page: nextPage,
    pageSize: nextSize,
    cancelStatus: status || undefined,
    orderId: applied.orderId.trim() || undefined,
    createTimeGe: dayStartSeconds(applied.startDate),
    createTimeLt: dayEndExclusiveSeconds(applied.endDate),
  }), [applied, pageSize, status]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      setLastSyncedAt('');
      return;
    }
    setLoading(true);
    try {
      const res = await tiktokPlatformAPI.listCancellations(shopId, queryParams(page));
      setRows(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
      setLastSyncedAt(res?.lastSyncedAt || '');
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast(e?.message || t('cancel.loadFail'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, page, queryParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    setDraft(EMPTY_QUERY);
    setApplied(EMPTY_QUERY);
    setStatus('');
  }, [shopId]);

  const handlePull = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'cancellation',
        shopId,
        label: t('tiktokList.pull'),
        payload: {
          createTimeGe: dayStartSeconds(draft.startDate),
          createTimeLt: dayEndExclusiveSeconds(draft.endDate),
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      if (page !== 1) setPage(1);
      else await load();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      showToast(e?.message || t('cancel.loadFail'), 'error');
    }
  }, [shopId, draft.startDate, draft.endDate, load, page, startJob, onTrackDone, t]);

  const { refresh: onPull, refreshing: pulling, cooldownSeconds } = useGuardedRefresh(handlePull, {
    minIntervalMs: 5000,
  });

  const decide = async (cancelId, action) => {
    if (!shopId || !cancelId) return;
    const approving = action === 'approve';
    const ok = await showTiktokConfirm({
      title: approving ? t('cancel.approveTitle') : t('cancel.rejectTitle'),
      message: approving
        ? t('cancel.approveMsg', { id: cancelId })
        : t('cancel.rejectMsg', { id: cancelId }),
      tone: 'warning',
      confirmText: approving ? t('returnList.approve') : t('returnList.reject'),
      cancelText: t('back'),
    });
    if (!ok) return;
    setBusyId(cancelId);
    try {
      if (approving) {
        await tiktokPlatformAPI.approveCancellation(shopId, cancelId);
        showToast(t('cancel.approved'), 'success');
      } else {
        await tiktokPlatformAPI.rejectCancellation(shopId, cancelId, {
          reject_reason: 'seller_reject',
        });
        showToast(t('cancel.rejected'), 'success');
      }
      await load();
    } catch (e) {
      showToast(e?.message || t('cancel.fail'), 'error');
    } finally {
      setBusyId('');
    }
  };

  const pageIds = rows.map(rowId).filter(Boolean);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const csvRows = useCallback((list) => list.map((row) => {
    const statusCode = String(row.status || row.cancel_status || '');
    return [
      rowId(row),
      row.orderNo || row.order_id || '',
      codedLabel(row.cancel_type || row.cancelType, TYPE_LABELS, TYPE_KEYS, t),
      codedLabel(row.role, ROLE_LABELS, ROLE_KEYS, t),
      statusLabel(statusCode, t),
      row.cancel_reason_text || row.cancelReasonText || row.cancel_reason || row.cancelReason || '',
      refundText(row),
      formatTime(row.createTime || row.create_time),
    ];
  }), [t]);

  const csvHeaders = [
    t('platformNav.cancellations'),
    t('cancel.colOrder'),
    t('cancel.colType'),
    t('cancel.colRole'),
    t('shop.colStatus'),
    t('cancel.colReason'),
    t('cancel.colRefund'),
    t('shop.colCreated'),
  ];

  const exportRows = async (list, emptyText) => {
    if (!list.length) {
      showToast(emptyText, 'warning');
      return;
    }
    downloadCsv(`tiktok-cancellations-${Date.now()}.csv`, csvHeaders, csvRows(list));
  };

  const exportPage = () => exportRows(rows, t('orderList.noneToExport'));

  const exportSelected = () => {
    const picked = rows.filter((row) => selectedIds.has(rowId(row)));
    exportRows(picked, t('orderList.noneToExport'));
  };

  const exportFiltered = async () => {
    if (!shopId) return;
    setExporting(true);
    try {
      const collected = [];
      let nextPage = 1;
      const size = 100;
      while (collected.length < 2000) {
        const res = await tiktokPlatformAPI.listCancellations(shopId, queryParams(nextPage, size));
        const items = Array.isArray(res?.items) ? res.items : [];
        collected.push(...items);
        const count = Number(res?.total) || collected.length;
        if (!items.length || collected.length >= count) break;
        nextPage += 1;
      }
      await exportRows(collected, t('orderList.noneToExport'));
    } catch (e) {
      showToast(e?.message || t('cancel.loadFail'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onQuery = () => {
    setApplied({ ...draft });
    setPage(1);
  };

  const statusTabs = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('cancel.statusAria')}>
      {STATUS_OPTIONS.map((opt) => (
        <button
          key={opt.value || 'all'}
          type="button"
          role="tab"
          aria-selected={status === opt.value}
          className={`tko-status-tab${status === opt.value ? ' tko-status-tab--active' : ''}`}
          onClick={() => {
            setStatus(opt.value);
            setPage(1);
          }}
        >
          {t(opt.labelKey, { defaultValue: opt.label })}
        </button>
      ))}
    </div>
  ), [status, t]);

  const latestSyncedLabel = lastSyncedAt
    ? t('orderList.lastUpdate', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : '';

  const syncing = pulling || isBusy('cancellation', shopId);

  return (
    <>
      <TiktokSyncProgressSlot kinds={['cancellation']} />
      <div className="tko-page">
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedIds.size}
            busy={loading || syncing || exporting}
            syncKinds={['cancellation']}
            onSync={() => (shopId ? onPull() : showToast(t('returnList.pickShop'), 'warning'))}
            syncing={syncing}
            syncCooldownSeconds={cooldownSeconds}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !shopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
          />
        )}
      >
        {shopDimensionTabs}
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <label className="tko-field tko-field--inline tko-field--w300">
              <span className="tko-field__label">{t('orderList.orderNo')}</span>
              <input
                className="tko-input"
                value={draft.orderId}
                disabled={!shopId}
                onChange={(e) => setDraft((prev) => ({ ...prev, orderId: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </label>
            <TiktokDateRangeFilter
              showTimeType={false}
              dateOnly
              startDate={draft.startDate}
              endDate={draft.endDate}
              disabled={!shopId}
              onStartDateChange={(value) => setDraft((prev) => ({ ...prev, startDate: pickDatePart(value) }))}
              onEndDateChange={(value) => setDraft((prev) => ({ ...prev, endDate: pickDatePart(value) }))}
            />
          </div>
          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--primary" disabled={!shopId || loading} onClick={onQuery}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>
      <ProductListCard
        tabs={statusTabs}
        loading={Boolean(shopId) && (loading || shopLoading)}
        loadingLabel={t('orderList.querying')}
      >
        <div className="tko-drafts__table-wrap tko-ops-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--cancel">
            <colgroup>
              <col className="tko-ops-col tko-ops-col--check" />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col className="tko-ops-col tko-ops-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="tko-ops-th tko-ops-th--check">
                  <input
                    type="checkbox"
                    aria-label={t('orderList.selectAll')}
                    checked={allSelected}
                    disabled={!pageIds.length}
                    onChange={togglePage}
                  />
                </th>
                <th>{t('platformNav.cancellations')}</th>
                <th>{t('cancel.colOrder')}</th>
                <th>{t('cancel.colType')}</th>
                <th>{t('cancel.colRole')}</th>
                <th>{t('shop.colStatus')}</th>
                <th>{t('cancel.colReason')}</th>
                <th>{t('cancel.colRefund')}</th>
                <th>{t('shop.colCreated')}</th>
                <th>{t('productTable.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="tko-board-table__empty">{t('cancel.empty')}</td>
                </tr>
              ) : null}
              {rows.map((row) => {
                const id = rowId(row);
                const statusCode = String(row.status || row.cancel_status || '');
                const pending = statusCode === 'CANCELLATION_REQUEST_PENDING';
                const reason = row.cancel_reason_text || row.cancelReasonText || row.cancel_reason || row.cancelReason || '—';
                return (
                  <tr key={id || row.orderNo}>
                    <td className="tko-ops-td tko-ops-td--check">
                      <input
                        type="checkbox"
                        aria-label={id}
                        checked={selectedIds.has(id)}
                        disabled={!id}
                        onChange={() => toggleOne(id)}
                      />
                    </td>
                    <td className="tko-board-table__code">{id || '—'}</td>
                    <td className="tko-board-table__code">{row.orderNo || row.order_id || '—'}</td>
                    <td>{codedLabel(row.cancel_type || row.cancelType, TYPE_LABELS, TYPE_KEYS, t)}</td>
                    <td>{codedLabel(row.role, ROLE_LABELS, ROLE_KEYS, t)}</td>
                    <td>{statusTag(statusCode, t)}</td>
                    <td>{reason}</td>
                    <td>{refundText(row)}</td>
                    <td>{formatTime(row.createTime || row.create_time)}</td>
                    <td>
                      {pending ? (
                        <div className="tko-board-actions">
                          <button
                            type="button"
                            className="tko-btn tko-btn--primary tko-btn--sm"
                            disabled={!id || busyId === id}
                            onClick={() => decide(id, 'approve')}
                          >
                            {t('returnList.approve')}
                          </button>
                          <button
                            type="button"
                            className="tko-btn tko-btn--secondary tko-btn--sm"
                            disabled={!id || busyId === id}
                            onClick={() => decide(id, 'reject')}
                          >
                            {t('returnList.reject')}
                          </button>
                        </div>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={(next) => setPage(next)}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
        />
      </ProductListCard>
    </div>
    </>
  );
}
